import { useState } from 'react';
import type { WisdomCard } from '@/mocks/wisdom';

interface IgStoryShareProps {
  card: WisdomCard;
}

const WEBSITE_URL = 'https://1mindlab-hugtree.com/';

const FONT_BODY = "'Noto Sans TC', 'PingFang TC', 'Microsoft JhengHei', sans-serif";
const FONT_HEADING = "'Fraunces', 'Noto Serif TC', 'Noto Sans TC', serif";

/* Wrap CJK text by measured width so the canvas never overflows */
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const chars = Array.from(text);
  const lines: string[] = [];
  let line = '';
  for (const ch of chars) {
    const test = line + ch;
    if (line && ctx.measureText(test).width > maxWidth) {
      lines.push(line);
      line = ch;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/* Draw a rounded rectangle path on the canvas */
function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/* Draw a clean Instagram camera-outline glyph */
function drawInstagramGlyph(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number) {
  const half = size / 2;
  ctx.strokeStyle = '#1C2A22';
  ctx.lineWidth = size * 0.09;
  roundRect(ctx, cx - half, cy - half, size, size, size * 0.28);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx, cy, size * 0.26, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx + size * 0.3, cy - size * 0.3, size * 0.09, 0, Math.PI * 2);
  ctx.fillStyle = '#1C2A22';
  ctx.fill();
}

/* Render a 9:16 (1080x1920) IG Story card to a PNG blob */
function renderStoryCard(card: WisdomCard): Promise<Blob> {
  const width = 1080;
  const height = 1920;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.reject(new Error('Canvas not supported'));

  // Warm organic sand background
  const grad = ctx.createLinearGradient(0, 0, 0, height);
  grad.addColorStop(0, '#F9F6F2');
  grad.addColorStop(0.55, '#F3EEE5');
  grad.addColorStop(1, '#EAE3D6');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, width, height);

  // Top accent bar
  ctx.fillStyle = '#1C2A22';
  ctx.fillRect(0, 0, width, 14);

  // Decorative concentric rings
  ctx.strokeStyle = 'rgba(28, 42, 34, 0.08)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(width / 2, 610, 300, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(width / 2, 610, 360, 0, Math.PI * 2);
  ctx.stroke();

  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';

  // Label
  ctx.fillStyle = 'rgba(28, 42, 34, 0.55)';
  ctx.font = `600 30px ${FONT_BODY}`;
  ctx.fillText('DAILY INSIGHT · 每日智慧', width / 2, 390);

  // Keyword
  ctx.fillStyle = '#1C2A22';
  ctx.font = `500 124px ${FONT_HEADING}`;
  ctx.fillText(card.keyword, width / 2, 620);

  // Divider
  ctx.fillStyle = 'rgba(28, 42, 34, 0.35)';
  ctx.fillRect(width / 2 - 42, 674, 84, 3);

  // Message
  ctx.fillStyle = '#1C2A22';
  ctx.font = `400 38px ${FONT_BODY}`;
  const messageLines = wrapText(ctx, `「${card.message}」`, 820);
  let y = 780;
  const messageLineHeight = 56;
  for (const line of messageLines) {
    ctx.fillText(line, width / 2, y);
    y += messageLineHeight;
  }

  // Practice
  if (card.practice) {
    y += 44;
    ctx.fillStyle = 'rgba(28, 42, 34, 0.5)';
    ctx.font = `600 26px ${FONT_BODY}`;
    ctx.fillText('今日小練習', width / 2, y);
    y += 46;
    ctx.fillStyle = 'rgba(28, 42, 34, 0.72)';
    ctx.font = `400 30px ${FONT_BODY}`;
    const practiceLines = wrapText(ctx, card.practice, 780);
    for (const line of practiceLines) {
      ctx.fillText(line, width / 2, y);
      y += 46;
    }
  }

  // Footer branding — soft clay badge pill (legible, no color clashing)
  const badgeCenterY = 1490;

  // Measure brand text segments so the @1mindlab handle can be emphasized
  const glyphSize = 34;
  const glyphGap = 16;
  const segGap = 4;

  ctx.font = `600 30px ${FONT_BODY}`;
  const prefixW = ctx.measureText('IG: ').width;
  ctx.font = `700 34px ${FONT_BODY}`;
  const handleW = ctx.measureText('@1mindlab').width;
  ctx.font = `600 30px ${FONT_BODY}`;
  const suffixW = ctx.measureText(' · 抱樹療癒').width;

  const groupW = glyphSize + glyphGap + prefixW + handleW + suffixW + segGap * 2;
  const groupX = width / 2 - groupW / 2;

  // Soft clay pill container around the brand line
  const badgePaddingX = 38;
  const badgePaddingY = 22;
  const badgeW = groupW + badgePaddingX * 2;
  const badgeH = glyphSize + badgePaddingY * 2;
  const badgeX = width / 2 - badgeW / 2;
  const badgeY = badgeCenterY - badgeH / 2;
  ctx.fillStyle = '#BCCBC0';
  roundRect(ctx, badgeX, badgeY, badgeW, badgeH, badgeH / 2);
  ctx.fill();

  drawInstagramGlyph(ctx, groupX + glyphSize / 2, badgeCenterY, glyphSize);

  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#1C2A22';
  let cursor = groupX + glyphSize + glyphGap;

  ctx.font = `600 30px ${FONT_BODY}`;
  ctx.fillText('IG: ', cursor + prefixW / 2, badgeCenterY);
  cursor += prefixW + segGap;

  ctx.font = `700 34px ${FONT_BODY}`;
  ctx.fillText('@1mindlab', cursor + handleW / 2, badgeCenterY);
  cursor += handleW + segGap;

  ctx.font = `600 30px ${FONT_BODY}`;
  ctx.fillText(' · 抱樹療癒', cursor + suffixW / 2, badgeCenterY);

  ctx.textBaseline = 'alphabetic';

  // Website URL below the badge — unboxed, 80% opacity graphite
  ctx.fillStyle = 'rgba(28, 42, 34, 0.8)';
  ctx.font = `400 28px ${FONT_BODY}`;
  ctx.fillText('https://1mindlab-hugtree.com/', width / 2, 1575);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Failed to render story card'));
    }, 'image/png');
  });
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function IgStoryShare({ card }: IgStoryShareProps) {
  const [busy, setBusy] = useState<'ig' | 'share' | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 4000);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(WEBSITE_URL);
    } catch {
      // ignore clipboard failures
    }
  };

  /* Share ONLY the rendered 9:16 image — no text captions or links attached */
  const handleShareImage = async (mode: 'ig' | 'share') => {
    if (busy) return;
    setBusy(mode);
    try {
      const blob = await renderStoryCard(card);
      const file = new File([blob], `1mindlab-wisdom-${card.id}.png`, { type: 'image/png' });

      const canShareFile =
        typeof navigator !== 'undefined' &&
        typeof navigator.share === 'function' &&
        typeof navigator.canShare === 'function' &&
        navigator.canShare({ files: [file] });

      if (canShareFile) {
        try {
          await navigator.share({ files: [file] });
          return;
        } catch (err) {
          if (err instanceof Error && err.name === 'AbortError') return;
          // Otherwise fall through to the download fallback below.
        }
      }

      // Graceful fallback: save the card + (for IG) copy the link + show a gentle toast
      downloadBlob(blob, `1mindlab-wisdom-${card.id}.png`);
      if (mode === 'ig') {
        await copyLink();
        showToast('卡片已儲存！開啟 IG 即可發佈 Story 🌿');
      } else {
        showToast('卡片已儲存！可開啟 WhatsApp、Signal 或其他 App 分享 🌿');
      }
    } catch {
      showToast('暫時未能產生卡片，請稍後再試。');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => handleShareImage('ig')}
        disabled={busy !== null}
        className="px-5 py-2.5 rounded-full text-sm font-semibold text-white transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 bg-[#B89C8F] hover:bg-[#A78B7E] disabled:opacity-60 disabled:cursor-not-allowed"
      >
        分享至
        <i className={busy === 'ig' ? 'ri-loader-4-line animate-spin' : 'ri-instagram-line'}></i>
      </button>
      <button
        type="button"
        onClick={() => handleShareImage('share')}
        disabled={busy !== null}
        className="px-5 py-2.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 bg-[#EFECE6] hover:bg-[#E8E3D9] text-[#1C2A22] disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <i className={busy === 'share' ? 'ri-loader-4-line animate-spin' : 'ri-share-forward-line'}></i>
        分享
      </button>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[120] max-w-[90vw] px-5 py-3 rounded-full bg-foreground-900/90 text-background-50 text-sm shadow-soft animate-[fadeIn_0.25s_ease-out] text-center">
          {toast}
        </div>
      )}
    </>
  );
}